import React, { useState } from 'react';
import { PersonCombobox, type PersonSelection } from './PersonCombobox';
import { VendorForm } from '@/features/people/vendors/components/VendorForm';
import { createVendorUser } from '@/features/people/vendors/store';
import { useUser } from '@/contexts/UserContext';

interface VendorFieldProps {
  /** the selected vendor's name (what the box shows) */
  value: string;
  onChange: (selection: PersonSelection) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** true: a typed name that is not in the list is allowed (a bid's subcontractor). false: a vendor must be picked. */
  allowFreeText?: boolean;
}

/**
 * THE Vendor field of every form (Bid, Bill, Bill Payment, ...): search the company's vendors, pick one, or "Create new"
 * -- which opens the Vendors page's own form and comes back with the new vendor selected. One implementation of the
 * wiring (PersonCombobox + VendorForm + createVendorUser + refreshing the list), so forms only say where the vendor goes.
 */
export function VendorField({ value, onChange, id, placeholder = 'Select or type vendor name', disabled, allowFreeText = true }: VendorFieldProps) {
  const { companyId, user } = useUser();
  const [isCreating, setIsCreating] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const handleCreate = async (vendorData: any): Promise<string | null> => {
    if (!companyId || !user?.id) throw new Error('Your session is not ready yet. Please try again in a moment.');
    const created = await createVendorUser(companyId, user.id, vendorData);
    onChange({ id: created.id, name: created.name });
    setRefreshToken((token) => token + 1);
    return created.id;
  };

  return (
    <>
      <PersonCombobox
        id={id}
        roles={['vendor']}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        allowFreeText={allowFreeText}
        onCreateNew={() => setIsCreating(true)}
        createLabel="Create new"
        refreshToken={refreshToken}
      />
      {/* The Vendor form is portaled to <body>, but React still bubbles ITS events through this tree: without this, saving
          the vendor would also submit the form this field sits in (a Bid, a Bill, ...). */}
      <div onSubmit={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <VendorForm isOpen={isCreating} vendor={null} onClose={() => setIsCreating(false)} onSave={handleCreate} />
      </div>
    </>
  );
}
